import { useCallback, useMemo, useRef, useState } from 'react';

import { Page } from 'constants/report-document-builder';
import ReportDocumentHeader from 'page-components/report-document-builder/DocumentBuilderHeader';
import MiniA4Block from 'page-components/report-document-builder/MiniA4Block';
import PageA4Block from 'page-components/report-document-builder/PageA4Block';
import PageCustomizationBlock from 'page-components/report-document-builder/PageCustomizationBlock';

const ReportDocumentBuilder = () => {
  const [title, setTitle] = useState<string>('New Document');
  const [pages, setPages] = useState<Page[]>([{ id: 1, html: '' }]);
  const [activeId, setActiveId] = useState<number>(1);
  const [isGridVisible, setIsGridVisible] = useState<boolean>(false);
  const nextIdRef = useRef(2);
  const activePage = useMemo(() => pages.find((page) => page.id === activeId), [pages, activeId]);

  const handleAddPage = useCallback(() => {
    const id = nextIdRef.current++;
    setPages((prev) => [...prev, { id, html: '' }]);
    setActiveId(id);
  }, []);

  const handleDeletePage = useCallback((id: number) => {
    setPages((prev) => {
      if (prev.length === 1) {
        return prev;
      }

      const index = prev.findIndex((page) => page.id === id);
      if (index === -1) {
        return prev;
      }

      const next = prev.filter((page) => page.id !== id);

      setActiveId((current) => (current === id ? next[Math.max(0, index - 1)]?.id ?? next[0].id : current));

      return next;
    });
  }, []);

  const handleUpdateActiveHtml = useCallback(
    (html: string) => {
      setPages((prev) => prev.map((p) => (p.id === activeId ? { ...p, html } : p)));
    },
    [activeId]
  );

  return (
    <div
      data-cy="report-document-builder-page"
      className="grid grid-rows-[auto_auto_auto_1fr] overflow-y-hidden h-[90vh]"
    >
      <ReportDocumentHeader title={title} onTitleChange={setTitle} />

      <div className="flex">
        <PageCustomizationBlock isGridVisible={isGridVisible} onToggleGrid={setIsGridVisible} />
      </div>

      <div className="grid grid-cols-[260px_1fr] gap-4 h-full min-h-0 overflow-hidden bg-neutral-50 dark:bg-neutral-800">
        <MiniA4Block
          pages={pages}
          activeId={activeId}
          onActivate={setActiveId}
          onAdd={handleAddPage}
          onDelete={handleDeletePage}
        />

        <PageA4Block
          html={activePage?.html ?? ''}
          onChangeHtml={handleUpdateActiveHtml}
          isGridVisible={isGridVisible}
        />
      </div>
    </div>
  );
};

export default ReportDocumentBuilder;
